import { useContext, useEffect, useReducer, useRef } from 'react';

import { SocketContext } from '@/contexts/rtm';
import { AuthorType, BrainActivity, BrainStreamEvent } from '@/shared/types/chat';
import { ServerToClientEvents, SocketEvent } from '@/shared/types/rtm';

import { EMPTY_SESSION_STREAM, sessionStreamReducer } from './sessionStreamReducer';

export const SESSION_STREAM_IDLE_MS = 60_000;
export const ACTIVITY_STALE_MS = 30_000;

export type UseRtmSessionStreamResult = { draft: string; activity: BrainActivity | null };

export const useRtmSessionStream = (sessionId: string | undefined): UseRtmSessionStreamResult => {
  const { socket, isConnected } = useContext(SocketContext);
  const [state, dispatch] = useReducer(sessionStreamReducer, EMPTY_SESSION_STREAM);
  const idleTimeout = useRef<NodeJS.Timeout | null>(null);
  const activityTimeouts = useRef(new Map<string, NodeJS.Timeout>());

  useEffect(() => {
    const timeouts = activityTimeouts.current;
    const clearActivityTimeouts = () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
    const clearAll = () => {
      clearTimeout(idleTimeout.current);
      clearActivityTimeouts();
      dispatch({ type: 'clear', sessionId: sessionId ?? null });
    };
    const trackActivity = (event: BrainStreamEvent) => {
      if (event.type === 'activity.started') {
        const activityId = event.data.activity_id;
        clearTimeout(timeouts.get(activityId));
        timeouts.set(
          activityId,
          setTimeout(() => dispatch({ type: 'activity_stale', activityId }), ACTIVITY_STALE_MS)
        );
      } else if (event.type === 'activity.finished') {
        clearTimeout(timeouts.get(event.data.activity_id));
        timeouts.delete(event.data.activity_id);
      }
    };

    // Also runs on a reconnect: events lost while disconnected would leave a gap in the draft.
    clearAll();
    if (!sessionId) {
      return;
    }

    const handleBrainStream: ServerToClientEvents[SocketEvent.messages_brain_stream] = ({
      data,
    }) => {
      if (data.session_id !== sessionId) {
        return;
      }
      dispatch({ type: 'event', event: data });
      trackActivity(data);
      clearTimeout(idleTimeout.current);
      idleTimeout.current = setTimeout(clearAll, SESSION_STREAM_IDLE_MS);
    };

    const handleMessageCreated: ServerToClientEvents[SocketEvent.messages_created] = ({ data }) => {
      if (data.session_id !== sessionId || data.author_type !== AuthorType.BRAIN) {
        return;
      }
      dispatch({ type: 'reply', requestId: data.request_id });
    };

    socket?.on(SocketEvent.messages_brain_stream, handleBrainStream);
    socket?.on(SocketEvent.messages_created, handleMessageCreated);
    return () => {
      socket?.off(SocketEvent.messages_brain_stream, handleBrainStream);
      socket?.off(SocketEvent.messages_created, handleMessageCreated);
      clearTimeout(idleTimeout.current);
      clearActivityTimeouts();
    };
  }, [sessionId, socket, isConnected]);

  if (state.sessionId !== (sessionId ?? null)) {
    return { draft: '', activity: null };
  }
  return { draft: Object.values(state.drafts).join(''), activity: state.activities.at(-1) ?? null };
};
